// Shared geometry for the header overlays (HeaderBrand, HeaderWishlist, LanguageSwitch,
// BrandHeaderBar). The header bar itself comes from the private @uag/react-core BaseLayout
// blueprint - there is no prop to inject content into it - so those components fake their
// placement with `position: fixed` elements sized/offset to match it.
//
// Best-effort pixel values: this sandbox has no access to the private @uag packages to preview the
// real header, so nudge them here (one place, all overlays stay aligned) after checking the
// rendered result.

/** Distance from the viewport top to the content row inside the header bar. */
export const HEADER_OVERLAY_TOP = 22;

/** Height of that content row - all overlays share it, so they share a vertical centre. */
export const HEADER_OVERLAY_ROW_HEIGHT = 66;

/** Full height of the header bar, i.e. what BrandHeaderBar has to cover to recolour it. */
export const HEADER_BAR_HEIGHT = HEADER_OVERLAY_TOP + HEADER_OVERLAY_ROW_HEIGHT;

/**
 * How far the header bar moved when FitToViewport pulled it flush to the window edges (reported:
 * "Header und Footer sollen bündig erscheinen ohne Einrückung" - the blueprint's nested layouts
 * inset it). FitToViewport measures the bar before and after and writes the differences into these
 * custom properties on <html>; the overlays add them to their own offsets, so they keep sitting
 * where they were tuned relative to the bar. Unset (0px) wherever nothing was moved.
 */
export const HEADER_SHIFT_LEFT_VAR = '--pih-header-dx';
export const HEADER_SHIFT_TOP_VAR = '--pih-header-dy';
export const HEADER_SHIFT_RIGHT_VAR = '--pih-header-dr';

/**
 * Where the header bar actually is after FitToViewport has measured (and flushed) it - left edge,
 * top edge and height, in px. BrandHeaderBar places the brand logo from these instead of guessed
 * offsets. Unset while no page has measured the bar.
 */
export const HEADER_BOX_LEFT_VAR = '--pih-header-box-left';
export const HEADER_BOX_TOP_VAR = '--pih-header-box-top';
export const HEADER_BOX_HEIGHT_VAR = '--pih-header-box-height';

/**
 * Set on <html> while FitToViewport has repainted the blueprint's header bar in the brand colour
 * itself (see FitToViewport's brandHeader). BrandHeaderBar then only adds the logo; without it
 * (a page that has no FitToViewport) it falls back to covering the bar.
 */
export const BRAND_HEADER_ATTR = 'data-pih-brand-header';

/** Radius of the header's top and the footer's bottom corners (reported: "ganz leicht abgerundete
 * Ecken im Header und Footer", as on the Umdasch Group Identity page). */
export const CHROME_RADIUS = '6px';

/** HEADER_OVERLAY_TOP, following the bar (see HEADER_SHIFT_TOP_VAR). */
export const HEADER_OVERLAY_TOP_CSS = `calc(${HEADER_OVERLAY_TOP}px + var(${HEADER_SHIFT_TOP_VAR}, 0px))`;

/** HEADER_BAR_HEIGHT, following the bar's top edge (see HEADER_SHIFT_TOP_VAR). */
export const HEADER_BAR_HEIGHT_CSS = `calc(${HEADER_BAR_HEIGHT}px + var(${HEADER_SHIFT_TOP_VAR}, 0px))`;

/** A left offset tuned against the bar's left edge, following it (see HEADER_SHIFT_LEFT_VAR). */
export const leftInset = (px: number) => `calc(${px}px + var(${HEADER_SHIFT_LEFT_VAR}, 0px))`;

/** z-index of the overlays; BrandHeaderBar sits one below so it paints behind them. */
export const HEADER_OVERLAY_Z_INDEX = 1000;

/**
 * Horizontal placement for the RIGHT-hand overlays (language select, Merkliste), given as the
 * distance from the right edge of the VIEWPORT to the overlay's left edge.
 *
 * Why `left: calc(100vw - Npx)` and not the obvious `right: Npx`: `position: fixed` resolves
 * against the viewport only as long as no ancestor establishes a containing block of its own - a
 * `transform`, `filter`, `perspective`, `contain` or `will-change` on any ancestor is enough, and
 * the private @uag/react-core BaseLayout/portal chrome has such an ancestor. Its box is wider than
 * the viewport (the page overflows horizontally), so `right: 100` measured 100px from THAT box's
 * right edge and pushed the language select off-screen (see chat: "Die Sprachauswahl im Header
 * schiebt 'rechts' aus dem Bildschirm raus"). `100vw` is viewport-relative whatever the containing
 * block does, so this holds either way. (100vw includes the scrollbar width, ~15px - the insets
 * below leave enough room for that.)
 */
export const rightInset = (px: number) => `calc(100vw - ${px}px + var(${HEADER_SHIFT_RIGHT_VAR}, 0px))`;

/** Left edge of the language select, measured from the right edge of the viewport. Nudged right
 * three times on request; the remaining clearance still has to clear the blueprint's own avatar,
 * which sits roughly 55px in from the right edge. */
export const LANGUAGE_SWITCH_INSET = 205;

/** Same for the Merkliste, far enough left to clear the language select next to it (that one is
 * 74px wide, so this leaves a ~40px gap between the two). Moved by the same 25px as the language
 * select above, so the gap between them stays put. */
export const WISHLIST_INSET = 375;

/**
 * z-index of the article/document detail overlay (DetailOverlay's Modal), above the faked header
 * overlays so it covers the header bar as well.
 */
export const DETAIL_OVERLAY_Z_INDEX = 1200;

/**
 * z-index every PORTALED dropdown (Select/MultiSelect combobox, Menu, Tooltip) has to use when it
 * can be opened from inside the detail overlay.
 *
 * Mantine portals those into `document.body` with a fixed default z-index of 300 - far below the
 * overlay's own 1200 - so inside the overlay they render BEHIND it: the control looks dead, clicks
 * land on the overlay instead. That was the bug behind "Beim Artikel funktionieren bei der
 * Dokumentliste die Filter 'Alle Sprachen' und 'Dokumentart' nicht. Bei Systemen klappt das." -
 * the very same components work outside the overlay, where 300 is plenty.
 */
export const OVERLAY_DROPDOWN_Z_INDEX = DETAIL_OVERLAY_Z_INDEX + 100;
